/**
 * SubmissionReview — full submission detail panel for staff.
 *
 * Features:
 *   - AI summary (on-demand "Generate summary") + severity-coded flag alerts
 *   - Toggle: Summary / Full Answers (Form.io read-only render)
 *   - Conversation transcript (expandable)
 *   - Generate PDF button
 *   - Send Message button (opens mailbox compose pre-filled)
 *   - Actionable contact-match review panel (confirm / reject / create-new / reassign)
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  forms as formsApi,
  pdf as pdfApi,
  ai,
  ghl,
  SubmissionDetail,
  SubmissionAttachmentMeta,
  GHLContactResult,
  MatchCandidate,
  ghlContactUrl,
} from '../../../lib/api.js';
import { AIFlag } from '@clearintake/shared';
import { useAuth } from '../../../lib/auth-context.js';
import {
  SparkleIcon, CheckIcon, CheckCircleIcon, XIcon, TriangleAlertIcon,
  InfoIcon, UserPlusIcon, SearchIcon, MailIcon, FileTextIcon,
  BotIcon, UserIcon, SpinnerIcon,
} from './icons.js';

interface Props {
  submissionId: string;
  onSendMessage?: (contactGhlId: string) => void;
}

type View = 'summary' | 'answers' | 'transcript';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/** Normalize a possibly-legacy flag shape ({label,message}) into an AIFlag. */
function toAIFlag(raw: unknown): AIFlag {
  const f = (raw ?? {}) as { severity?: string; label?: string; message?: string; field?: string };
  const severity: AIFlag['severity'] =
    f.severity === 'critical' || f.severity === 'warning' || f.severity === 'info'
      ? f.severity
      : 'warning';
  const message = f.message ?? f.label ?? String(raw);
  const out: AIFlag = { severity, message };
  if (f.field) out.field = f.field;
  return out;
}

export default function SubmissionReview({ submissionId, onSendMessage }: Props): React.ReactElement {
  const { user } = useAuth();
  const locationId = user?.locationId ?? '';
  const [detail,     setDetail]    = useState<SubmissionDetail | null>(null);
  const [loading,    setLoading]   = useState(true);
  const [error,      setError]     = useState<string | null>(null);
  // Full Answers is the default view — staff want the actual submitted data first;
  // the AI Summary tab is one click away.
  const [view,       setView]      = useState<View>('answers');
  const [pdfLoading, setPdfLoad]   = useState(false);
  const [pdfError,   setPdfError]  = useState<string | null>(null);

  // Lifted AI summary + flags state (so generated content appears without reload)
  const [aiSummary,   setAiSummary]   = useState<string | null>(null);
  const [aiFlags,     setAiFlags]     = useState<AIFlag[]>([]);
  const [summarizing, setSummarizing] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await formsApi.submissions.get(submissionId);
      if (res.success && res.data) {
        setDetail(res.data);
      } else {
        setError(res.error ?? 'Failed to load submission');
      }
    } catch {
      setError('Failed to load submission');
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => { void load(); }, [load]);

  // Seed lifted summary/flags whenever the loaded detail changes.
  useEffect(() => {
    if (!detail) return;
    setAiSummary(detail.aiSummary);
    setAiFlags(Array.isArray(detail.aiFlags) ? detail.aiFlags.map(toAIFlag) : []);
    setSummaryError(null);
  }, [detail]);

  const handlePdf = async (force = false) => {
    setPdfLoad(true); setPdfError(null);
    const res = await pdfApi.generate({ type: 'form_submission', entityId: submissionId, force });
    if (res.success && res.data) window.open(res.data.url, '_blank');
    else setPdfError(res.error ?? 'PDF generation failed');
    setPdfLoad(false);
  };

  const handleGenerateSummary = useCallback(async (force = false) => {
    setSummarizing(true);
    setSummaryError(null);
    try {
      const res = await ai.summarize(submissionId, force);
      if (res.success && res.data) {
        setAiSummary(res.data.summary);
        setAiFlags(Array.isArray(res.data.flags) ? res.data.flags.map(toAIFlag) : []);
      } else {
        setSummaryError(res.error ?? 'Could not generate summary');
      }
    } catch {
      setSummaryError('Could not generate summary');
    } finally {
      setSummarizing(false);
    }
  }, [submissionId]);

  if (loading) return <div style={styles.center}><p style={styles.muted}>Loading submission…</p></div>;
  if (error || !detail) return <div style={styles.center}><p style={{ color: '#c0392b' }}>{error ?? 'Not found'}</p></div>;

  const hasTranscript = detail.transcript && (detail.transcript as unknown[]).length > 0;

  const d = detail.data;
  const contactName = [
    d['firstName'] ?? d['first_name'] ?? d['patientFirstName'] ?? d['patientfirstname'],
    d['lastName']  ?? d['last_name']  ?? d['patientLastName']  ?? d['patientlastname'],
  ].filter(Boolean).join(' ') || null;

  // First-class owner identity (stamped submissions). Old rows have no submitter
  // and fall back to the contactName heuristic in the meta line above.
  const owner     = detail.submitter;
  const ownerName = owner && (owner.firstName || owner.lastName)
    ? [owner.lastName, owner.firstName].filter(Boolean).join(', ')
    : null;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.formName}>{detail.formName}</h2>
          <p style={styles.meta}>
            Submitted {formatDate(detail.submittedAt)}
            {(contactName ?? detail.contactGhlId) && (
              <>
                {' · '}
                {detail.contactGhlId ? (
                  <a
                    href={ghlContactUrl(locationId, detail.contactGhlId)}
                    target="_blank"
                    rel="noreferrer"
                    style={styles.contactLink}
                  >
                    {contactName ?? detail.contactGhlId}
                  </a>
                ) : (contactName)}
              </>
            )}
          </p>
          {owner && (ownerName || owner.dob) && (
            <p style={styles.ownerLine}>
              {ownerName}
              {owner.dob && (ownerName ? ` · DOB ${owner.dob}` : `DOB ${owner.dob}`)}
              <span style={owner.source === 'patient' ? styles.badgePatient : styles.badgeContact}>
                {owner.source === 'patient' ? 'Patient-entered' : 'From contact'}
              </span>
            </p>
          )}
        </div>
        <div style={styles.actions}>
          {detail.contactGhlId && onSendMessage && (
            <button style={styles.secondaryBtn} onClick={() => onSendMessage(detail.contactGhlId!)}>
              <span style={styles.btnInner}><MailIcon size={15} />Send Message</span>
            </button>
          )}
          <button style={styles.secondaryBtn} onClick={() => handlePdf(false)} disabled={pdfLoading} aria-busy={pdfLoading}>
            {pdfLoading ? (
              <span style={styles.btnInner}><SpinnerIcon size={15} />Generating…</span>
            ) : (
              <span style={styles.btnInner}><FileTextIcon size={15} />PDF</span>
            )}
          </button>
          <button
            style={styles.secondaryBtn}
            onClick={() => handlePdf(true)}
            disabled={pdfLoading}
            title="Regenerate PDF (ignore cached copy)"
            aria-label="Regenerate PDF"
          >
            <span style={styles.btnInner}>↻</span>
          </button>
        </div>
      </div>
      {pdfError && <div style={styles.errorBanner}>{pdfError}</div>}

      {/* AI Flags */}
      {aiFlags.length > 0 && (
        <div style={styles.flagsBlock} role="alert">
          <p style={styles.flagsTitle}>
            <span style={styles.flagsTitleIcon}><TriangleAlertIcon size={14} /></span>
            Clinical flags
          </p>
          {aiFlags.map((f, i) => (
            <FlagRow key={i} flag={f} />
          ))}
        </div>
      )}

      {/* Contact match review */}
      {detail.matchReviewStatus === 'pending' && (
        <MatchReviewPanel
          submissionId={submissionId}
          locationId={locationId}
          matchConfidence={detail.matchConfidence}
          onResolved={() => { void load(); }}
        />
      )}

      {/* View toggle */}
      <div style={styles.viewToggle}>
        {(['summary', 'answers', ...(hasTranscript ? ['transcript'] : [])] as View[]).map((v) => (
          <button
            key={v}
            style={{ ...styles.viewBtn, ...(view === v ? styles.viewBtnActive : {}) }}
            onClick={() => setView(v)}
          >
            {v === 'summary' ? 'AI Summary' : v === 'answers' ? 'Full Answers' : 'Conversation'}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={styles.content}>
        {view === 'summary' && (
          <SummaryView
            summary={aiSummary}
            loading={summarizing}
            error={summaryError}
            onGenerate={handleGenerateSummary}
          />
        )}
        {view === 'answers' && (
          <>
            <AttachmentsPanel submissionId={detail.id} />
            <AnswersView data={detail.data} schemaJson={detail.schemaJson} />
          </>
        )}
        {view === 'transcript' && hasTranscript && (
          <TranscriptView transcript={detail.transcript as { role: string; content: string; timestamp?: string }[]} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function FlagRow({ flag }: { flag: AIFlag }): React.ReactElement {
  const set =
    flag.severity === 'critical' ? styles.flagCritical
    : flag.severity === 'info'   ? styles.flagInfo
    : styles.flagWarning;
  const Icon = flag.severity === 'info' ? InfoIcon : TriangleAlertIcon;
  return (
    <div style={{ ...styles.flag, ...set }}>
      <span style={styles.flagIcon}><Icon size={13} /></span>
      <span>
        {flag.field && <strong>{flag.field}: </strong>}
        {flag.message}
      </span>
    </div>
  );
}

function SummaryView({
  summary, loading, error, onGenerate,
}: {
  summary: string | null;
  loading: boolean;
  error: string | null;
  onGenerate: (force?: boolean) => void;
}): React.ReactElement {
  const isEmpty = !summary || summary === 'viewed';

  if (loading) {
    return (
      <div style={styles.summaryState}>
        <div style={styles.summaryIconCircle}><SparkleIcon size={32} /></div>
        <button style={styles.primaryBtn} disabled aria-busy={true}>
          <span style={styles.btnInner}><SpinnerIcon size={16} />Summarizing…</span>
        </button>
        <p style={styles.summaryStateSub}>Reading answers and flagging clinical concerns…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.summaryState}>
        <div style={styles.errorBannerInline} role="alert">{error}</div>
        <button style={styles.linkBtn} onClick={() => onGenerate()}>Try again</button>
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div style={styles.summaryState}>
        <div style={styles.summaryIconCircle}><SparkleIcon size={32} /></div>
        <p style={styles.summaryStateTitle}>No summary yet</p>
        <p style={styles.summaryStateSub}>
          Generate an AI clinical summary of this submission. Patient identifiers are stripped before processing.
        </p>
        <button style={styles.primaryBtn} onClick={() => onGenerate()}>
          <span style={styles.btnInner}><SparkleIcon size={16} />Generate summary</span>
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={styles.summaryText}>
        {summary.split('\n').map((line, i) => (
          <p key={i} style={{ margin: '0 0 8px' }}>{line}</p>
        ))}
      </div>
      <div style={styles.summaryFooter}>
        <span style={styles.summaryFooterIcon}><InfoIcon size={12} /></span>
        Generated by AI · review before clinical use
        <button style={styles.regenBtn} onClick={() => onGenerate(true)} title="Regenerate this summary">
          <SparkleIcon size={12} /> Regenerate
        </button>
      </div>
    </div>
  );
}

function AnswersView({ data, schemaJson }: { data: Record<string, unknown>; schemaJson: unknown }): React.ReactElement {
  const [FormRenderer, setRenderer] = useState<React.ComponentType<{ form: unknown; submission: { data: unknown }; options: Record<string, unknown> }> | null>(null);

  useEffect(() => {
    import('@formio/react').then((mod) => {
      setRenderer(() => (mod as { Form: typeof FormRenderer }).Form);
    }).catch(() => { /* no formio — fall back */ });
  }, []);

  if (FormRenderer && schemaJson) {
    return (
      <div className="ci-formio-review" style={styles.formioWrap}>
        <FormRenderer
          form={schemaJson}
          submission={{ data }}
          options={{ readOnly: true, noAlerts: true }}
        />
      </div>
    );
  }

  return (
    <div style={styles.kvTable}>
      {Object.entries(data).map(([k, v]) => (
        <div key={k} style={styles.kvRow}>
          <span style={styles.kvKey}>{k}</span>
          <span style={styles.kvVal}>{typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Attachments panel — lists a submission's files (native uploads + legacy-migrated) via
 * the authoritative listing endpoint, and downloads each through an authenticated JSON
 * fetch that returns a short-lived SAS (no bearer/capability token ever in the DOM).
 * Renders nothing when the submission has no attachments.
 */
function AttachmentsPanel({ submissionId }: { submissionId: string }): React.ReactElement | null {
  const [items, setItems] = useState<SubmissionAttachmentMeta[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    formsApi.submissions.getAttachments(submissionId)
      .then((res) => { if (live && res.success && res.data) setItems(res.data.attachments); })
      .catch(() => { /* non-fatal — panel just hides */ })
      .finally(() => { if (live) setLoaded(true); });
    return () => { live = false; };
  }, [submissionId]);

  const openFile = useCallback(async (id: string, filename: string) => {
    setBusyId(id);
    try {
      const res = await formsApi.submissions.getFileUrl(id);
      if (res.success && res.data?.url) {
        // Trigger via a temporary anchor, NOT window.open — an async window.open (after the
        // await, outside the click gesture) is killed by popup blockers. The SAS already
        // carries Content-Disposition: attachment, so this downloads the file.
        const a = document.createElement('a');
        a.href = res.data.url;
        a.download = filename;
        a.rel = 'noopener';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch { /* ignore — a failed open leaves the row unchanged */ }
    finally { setBusyId(null); }
  }, []);

  if (!loaded || items.length === 0) return null;
  return (
    <div style={styles.attachBlock}>
      <p style={styles.attachTitle}>Attachments ({items.length})</p>
      {items.map((a) => (
        <button
          key={a.id}
          type="button"
          onClick={() => openFile(a.id, a.filename)}
          disabled={busyId === a.id}
          style={styles.attachRow}
        >
          <FileTextIcon size={14} />
          <span style={styles.attachName}>{a.filename}</span>
          <span style={styles.attachSize}>{busyId === a.id ? '…' : `${Math.max(1, Math.round(a.sizeBytes / 1024))} KB`}</span>
        </button>
      ))}
    </div>
  );
}

function TranscriptView({ transcript }: { transcript: { role: string; content: string; timestamp?: string }[] }): React.ReactElement {
  return (
    <div style={styles.transcript}>
      {transcript.map((msg, i) => {
        const isAI = msg.role === 'assistant';
        return (
          <div key={i} style={{ ...styles.tMsg, ...(isAI ? styles.tMsgAI : styles.tMsgUser) }}>
            <span style={styles.tRole}>
              <span style={styles.tRoleIcon}>{isAI ? <BotIcon size={14} /> : <UserIcon size={14} />}</span>
              {isAI ? 'AI' : 'Patient'}
            </span>
            <p style={styles.tContent}>{msg.content}</p>
            {msg.timestamp && <span style={styles.tTime}>{formatDate(msg.timestamp)}</span>}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contact-match resolve panel
// ---------------------------------------------------------------------------

type Resolved = null | 'linked' | 'rejected' | 'created';

function confidencePillStyle(pct: number): React.CSSProperties {
  if (pct >= 80) return { background: '#ecfdf5', color: '#047857' };
  if (pct >= 50) return { background: '#fffbeb', color: '#92400e' };
  return { background: '#fef2f2', color: '#b91c1c' };
}

function MatchReviewPanel({
  submissionId, locationId, matchConfidence, onResolved,
}: {
  submissionId: string;
  locationId: string;
  matchConfidence: number | null;
  onResolved: () => void;
}): React.ReactElement {
  const [candidates,   setCandidates]   = useState<MatchCandidate[]>([]);
  const [matchLoading, setMatchLoading] = useState(true);
  const [matchError,   setMatchError]   = useState<string | null>(null);
  const [resolving,    setResolving]    = useState(false);
  const [resolved,     setResolved]     = useState<Resolved>(null);
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignQuery, setReassignQuery] = useState('');
  const [reassignResults, setReassignResults] = useState<GHLContactResult[]>([]);
  const [reassignSearching, setReassignSearching] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchedRef = useRef(false);

  // Auto-fetch candidates once on mount.
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    let cancelled = false;
    (async () => {
      setMatchLoading(true);
      setMatchError(null);
      try {
        const res = await ai.matchContact(submissionId);
        if (cancelled) return;
        if (res.success && res.data) {
          setCandidates(Array.isArray(res.data.candidates) ? res.data.candidates : []);
        } else {
          setMatchError(res.error ?? 'Could not load match candidates');
        }
      } catch {
        if (!cancelled) setMatchError('Could not load match candidates');
      } finally {
        if (!cancelled) setMatchLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [submissionId]);

  // Debounced reassign search.
  useEffect(() => {
    if (!reassignOpen) return;
    if (reassignQuery.trim().length < 2) { setReassignResults([]); return; }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setReassignSearching(true);
      try {
        const res = await ghl.searchContacts(reassignQuery.trim());
        if (res.success && res.data) setReassignResults(res.data.contacts.slice(0, 5));
        else setReassignResults([]);
      } catch {
        setReassignResults([]);
      } finally {
        setReassignSearching(false);
      }
    }, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [reassignQuery, reassignOpen]);

  const resolve = useCallback(async (
    action: 'confirm' | 'reject' | 'create_new',
    contactGhlId?: string,
  ) => {
    setResolving(true);
    setMatchError(null);
    try {
      const payload = contactGhlId
        ? { submissionId, action, contactGhlId }
        : { submissionId, action };
      const res = await ai.confirmMatch(payload);
      if (res.success) {
        setResolved(action === 'reject' ? 'rejected' : action === 'create_new' ? 'created' : 'linked');
        onResolved();
      } else {
        setMatchError(res.error ?? 'Could not resolve the match');
      }
    } catch {
      setMatchError('Could not resolve the match');
    } finally {
      setResolving(false);
    }
  }, [submissionId, onResolved]);

  if (resolved) {
    const msg =
      resolved === 'rejected' ? 'Match rejected — submission left unlinked.'
      : resolved === 'created' ? 'New contact created and linked.'
      : 'Contact linked.';
    return (
      <div style={styles.successStrip} role="status">
        <span style={styles.successStripIcon}><CheckCircleIcon size={15} /></span>
        {msg}
      </div>
    );
  }

  const pct = matchConfidence !== null ? Math.round(matchConfidence * 100) : null;
  const noCandidates = !matchLoading && candidates.length === 0;

  return (
    <div style={styles.matchBlock}>
      <div style={styles.matchHeader}>
        <p style={styles.matchTitle}>Contact match needs review</p>
        {pct !== null && (
          <span style={{ ...styles.confidencePill, ...confidencePillStyle(pct) }}>
            {pct}%
          </span>
        )}
      </div>

      {matchError && <div style={styles.errorBannerInline} role="alert">{matchError}</div>}

      {/* Candidate list / skeletons */}
      {matchLoading ? (
        <div style={styles.candidateList}>
          {[0, 1, 2].map((i) => <div key={i} style={styles.skeletonRow} />)}
        </div>
      ) : noCandidates ? (
        <p style={styles.matchSub}>No likely matches found.</p>
      ) : (
        <div style={styles.candidateList}>
          {candidates.map((c) => {
            const cPct = Math.round((c.confidence ?? 0) * 100);
            return (
              <div key={c.contactId} style={styles.candidateCard}>
                <div style={styles.candidateMeta}>
                  <span style={styles.candidateName}>{c.name || c.contactId}</span>
                  <span style={styles.candidateContact}>
                    {[c.email, c.phone].filter(Boolean).join(' · ')}
                  </span>
                </div>
                <div style={styles.candidateRight}>
                  <span style={{ ...styles.confidencePillSm, ...confidencePillStyle(cPct) }}>{cPct}%</span>
                  <button
                    style={styles.confirmBtn}
                    disabled={resolving}
                    onClick={() => resolve('confirm', c.contactId)}
                  >
                    <span style={styles.btnInner}><CheckIcon size={13} />Confirm</span>
                  </button>
                  <a
                    href={ghlContactUrl(locationId, c.contactId)}
                    target="_blank"
                    rel="noreferrer"
                    style={styles.openLink}
                  >
                    Open contact
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Panel-level actions */}
      <div style={styles.matchActions}>
        <button style={styles.matchActionBtn} disabled={resolving} onClick={() => resolve('reject')}>
          <span style={styles.btnInner}><XIcon size={13} />Reject match</span>
        </button>
        {confirmCreate ? (
          <span style={styles.inlineConfirm}>
            Create a new contact from this submission?
            <button style={styles.confirmBtn} disabled={resolving} onClick={() => { setConfirmCreate(false); void resolve('create_new'); }}>
              Confirm
            </button>
            <button style={styles.matchActionBtn} disabled={resolving} onClick={() => setConfirmCreate(false)}>
              Cancel
            </button>
          </span>
        ) : (
          <button style={styles.matchActionBtn} disabled={resolving} onClick={() => setConfirmCreate(true)}>
            <span style={styles.btnInner}><UserPlusIcon size={13} />Create new contact</span>
          </button>
        )}
        <button style={styles.matchActionBtn} disabled={resolving} onClick={() => setReassignOpen((v) => !v)}>
          <span style={styles.btnInner}><SearchIcon size={13} />Reassign…</span>
        </button>
      </div>

      {/* Reassign sub-panel */}
      {reassignOpen && (
        <div style={styles.reassignPanel}>
          <label htmlFor="ci-reassign-search" style={styles.srOnly}>Search contacts</label>
          <input
            id="ci-reassign-search"
            style={styles.reassignInput}
            placeholder="Search by name, email, or phone"
            value={reassignQuery}
            onChange={(e) => setReassignQuery(e.target.value)}
          />
          {reassignQuery.trim().length < 2 ? (
            <p style={styles.reassignHint}>Search by name, email, or phone</p>
          ) : reassignSearching ? (
            <p style={styles.reassignHint}>Searching…</p>
          ) : reassignResults.length === 0 ? (
            <p style={styles.reassignHint}>No contacts found</p>
          ) : (
            <div style={styles.reassignResults}>
              {reassignResults.map((r) => (
                <div key={r.id} style={styles.candidateCard}>
                  <div style={styles.candidateMeta}>
                    <span style={styles.candidateName}>{[r.firstName, r.lastName].filter(Boolean).join(' ') || r.id}</span>
                    <span style={styles.candidateContact}>{[r.email, r.phone].filter(Boolean).join(' · ')}</span>
                  </div>
                  <button
                    style={styles.confirmBtn}
                    disabled={resolving}
                    onClick={() => resolve('confirm', r.id)}
                  >
                    Select
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  attachBlock: { marginBottom: 16, padding: '12px 14px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' },
  attachTitle: { margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: '#111827' },
  attachRow:   { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 6, background: '#fff', cursor: 'pointer', color: '#2563eb', fontSize: 13, textAlign: 'left' },
  attachName:  { flex: 1, wordBreak: 'break-word' },
  attachSize:  { color: '#9ca3af', fontSize: 12, flexShrink: 0 },
  container:  { display: 'flex', flexDirection: 'column', height: '100%', background: '#fff' },
  center:     { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' },
  muted:      { color: '#9ca3af', fontSize: 14 },
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px 20px', borderBottom: '1px solid #e5e7eb' },
  formName:   { margin: '0 0 4px', fontSize: 17, fontWeight: 600, color: '#111827' },
  meta:       { margin: 0, fontSize: 13, color: '#6b7280' },
  ownerLine:  { margin: '4px 0 0', fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  badgeContact: { background: '#eff6ff', color: '#2563eb', fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 999 },
  badgePatient: { background: '#f0fdf4', color: '#15803d', fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 999 },
  contactLink: { color: '#2563eb', textDecoration: 'underline' },
  actions:    { display: 'flex', gap: 8, flexWrap: 'wrap' as const },
  secondaryBtn: { background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13, color: '#374151' },
  btnInner:   { display: 'inline-flex', alignItems: 'center', gap: 6 },
  errorBanner: { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '8px 20px', color: '#b91c1c', fontSize: 13, margin: '0 20px 8px' },
  errorBannerInline: { background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '8px 12px', color: '#b91c1c', fontSize: 13, marginBottom: 8 },

  // Flags
  flagsBlock: { margin: '0 20px 12px', padding: '12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 },
  flagsTitle: { margin: '0 0 8px', fontWeight: 600, color: '#111827', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 },
  flagsTitleIcon: { display: 'flex', alignItems: 'center', color: '#b91c1c' },
  flag:       { display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 13, marginBottom: 6, padding: '6px 8px', borderRadius: 6, border: '1px solid transparent' },
  flagIcon:   { display: 'flex', alignItems: 'center', marginTop: 1, flexShrink: 0 },
  flagCritical: { background: '#fef2f2', borderColor: '#fca5a5', color: '#b91c1c' },
  flagWarning:  { background: '#fffbeb', borderColor: '#fcd34d', color: '#92400e' },
  flagInfo:     { background: '#eff6ff', borderColor: '#bfdbfe', color: '#1e40af' },

  // Match panel
  matchBlock: { margin: '0 20px 12px', padding: '12px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10 },
  matchHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 },
  matchTitle: { margin: 0, fontWeight: 600, color: '#92400e', fontSize: 14 },
  matchSub:   { margin: '4px 0 8px', fontSize: 13, color: '#6b7280' },
  confidencePill: { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999 },
  confidencePillSm: { fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999 },
  candidateList: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 },
  skeletonRow: { height: 44, borderRadius: 8, background: '#f1f5f9' },
  candidateCard: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', flexWrap: 'wrap' as const },
  candidateMeta: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 140 },
  candidateName: { fontSize: 13, fontWeight: 600, color: '#111827' },
  candidateContact: { fontSize: 12, color: '#6b7280' },
  candidateRight: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const },
  confirmBtn: { display: 'inline-flex', alignItems: 'center', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 500 },
  openLink: { fontSize: 12, color: '#2563eb', textDecoration: 'underline' },
  matchActions: { display: 'flex', gap: 8, flexWrap: 'wrap' as const, alignItems: 'center' },
  matchActionBtn: { background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12, color: '#374151' },
  inlineConfirm: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#92400e' },
  reassignPanel: { marginTop: 10, paddingTop: 10, borderTop: '1px solid #fcd34d' },
  reassignInput: { width: '100%', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, color: '#111827', boxSizing: 'border-box' as const },
  reassignHint: { margin: '6px 0 0', fontSize: 13, color: '#6b7280' },
  reassignResults: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8, maxHeight: 240, overflowY: 'auto' },
  srOnly: { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap', border: 0 },

  successStrip: { display: 'flex', alignItems: 'center', gap: 8, margin: '0 20px 12px', padding: '8px 12px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, color: '#047857', fontSize: 13, fontWeight: 500 },
  successStripIcon: { display: 'flex', alignItems: 'center' },

  // View toggle + content
  viewToggle: { display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '0 20px' },
  viewBtn: { padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: '#6b7280' },
  viewBtnActive: { color: '#2563eb', borderBottom: '2px solid #2563eb' },
  content:    { flex: 1, overflowY: 'auto', padding: '16px 20px 40px' },

  // Summary states
  summaryState: { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '32px 16px', gap: 10 },
  summaryIconCircle: { width: 56, height: 56, borderRadius: '50%', background: '#eff6ff', color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  summaryStateTitle: { margin: 0, fontSize: 14, fontWeight: 600, color: '#111827' },
  summaryStateSub: { margin: 0, fontSize: 13, color: '#6b7280', maxWidth: 360, lineHeight: 1.5 },
  primaryBtn: { display: 'inline-flex', alignItems: 'center', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 500 },
  linkBtn: { background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: 0 },
  summaryText: { fontSize: 14, color: '#111827', lineHeight: 1.7 },
  summaryFooter: { display: 'flex', alignItems: 'center', gap: 5, marginTop: 12, fontSize: 11, color: '#9ca3af' },
  summaryFooterIcon: { display: 'flex', alignItems: 'center' },
  regenBtn: { marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 11, color: '#2563eb', fontWeight: 500 },

  placeholder: { color: '#9ca3af', fontSize: 14 },
  formioWrap: { maxWidth: '100%' },
  kvTable:    { display: 'flex', flexDirection: 'column', gap: 8 },
  kvRow:      { display: 'flex', gap: 12, fontSize: 13, padding: '6px 0', borderBottom: '1px solid #f3f4f6' },
  kvKey:      { fontWeight: 600, color: '#374151', minWidth: 160, flexShrink: 0 },
  kvVal:      { color: '#111827', flex: 1, wordBreak: 'break-word' },
  transcript: { display: 'flex', flexDirection: 'column', gap: 10 },
  tMsg:       { borderRadius: 8, padding: '10px 14px', maxWidth: '80%' },
  tMsgAI:     { background: '#eff6ff', border: '1px solid #bfdbfe', alignSelf: 'flex-start' },
  tMsgUser:   { background: '#f9fafb', border: '1px solid #e5e7eb', alignSelf: 'flex-end' },
  tRole:      { fontSize: 11, fontWeight: 600, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 5 },
  tRoleIcon:  { display: 'flex', alignItems: 'center' },
  tContent:   { margin: '4px 0 0', fontSize: 13, color: '#111827', lineHeight: 1.6, whiteSpace: 'pre-wrap' },
  tTime:      { fontSize: 10, color: '#9ca3af' },
};
